import { inject, Injectable, signal, NgZone } from '@angular/core';
import { from, lastValueFrom, mergeMap, map, catchError, of } from 'rxjs';
import { environment } from '../../../environments/environment';
import { TranslateService } from '@ngx-translate/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { FolderDetails } from '../../shared/model/folder-details-interface';
import { CONSTANTS_DETAILS } from '../../shared';
import {
  FileWithPath,
  UploadResponse,
  ChunkRequest,
  ChunkResult,
  ChunkConfig,
  MergeResponse,
} from '../../shared/model';
import { DialogService } from '../../shared/services/dialog.service';
import { createCRC32 } from 'hash-wasm';
import { SnackbarService } from '../snackbar/snackbar.service';

@Injectable({
  providedIn: 'root',
})
export class AppService {
  public folderPath = '';
  public selectedStorageOption = signal<string>(
    CONSTANTS_DETAILS.DEFAULT_STORAGE,
  );
  public folderPathSignal = signal<string>('');
  private baseUrl = environment.APP_BASE_URL;
  private _translateService = inject(TranslateService);
  private http = inject(HttpClient);
  public uploadProgressSignal = signal<number>(0);
  public uploadCompleteSignal = signal<boolean>(false);
  public isIndeterminateProgressSignal = signal<boolean>(false);
  public isIndeterminateProgressSignalHash = signal<boolean>(false);
  public _dialogService = inject(DialogService);
  private hasher!: Awaited<ReturnType<typeof createCRC32>>;
  private ngZone = inject(NgZone);
  private _snackbarService = inject(SnackbarService);

  /**
   * Initializes the CRC32C hasher outside Angular's zone to avoid triggering change detection.
   *
   * @memberof AppService
   */
  async initHasher() {
    const CRC32C_POLY = 0x82f63b78;
    await this.ngZone.runOutsideAngular(async () => {
      this.hasher = await createCRC32(CRC32C_POLY);
      this.hasher.init();
    });
  }

  /**
   * Updates the folder path and notifies components
   * @param path - The new folder path
   * @memberof AppService
   */
  public updateFolderPath(path: string): void {
    this.folderPath = path;
    this.folderPathSignal.set(path);
  }

  /**
   * Fetches folder data based on the path.
   *
   * @return {*}  {Observable<any>}
   * @memberof AppService
   */
  public getParentFolderData(): Promise<FolderDetails[]> {
    return lastValueFrom(
      this.http.get<FolderDetails[]>(
        this.baseUrl +
          CONSTANTS_DETAILS.FOLDER_BASE_URL +
          CONSTANTS_DETAILS.GET_FOLDER_ITEMS_URL,
      ),
    ).catch((err) => {
      this._dialogService.commonDialog({
        iconType: 'error',
        message:
          this._translateService.instant('folderDataFetchError') + err.message,
        singleButton: true,
      });
      return [];
    });
  }

  /**
   * Fetches folder data based on the path.
   *
   * @param folderPath
   * @return {*}  {Observable<any>}
   * @memberof AppService
   */
  public getFolderData(): Promise<FolderDetails[]> {
    return lastValueFrom(
      this.http.get<FolderDetails[]>(
        this.baseUrl +
          CONSTANTS_DETAILS.FOLDER_BASE_URL +
          CONSTANTS_DETAILS.GET_FOLDER_ITEMS_URL +
          `${this.folderPath}`,
      ),
    ).catch((err) => {
      this._dialogService.commonDialog({
        iconType: 'error',
        message:
          this._translateService.instant('folderDataFetchError') + err.message,
        singleButton: true,
      });
      return [];
    });
  }

  /**
   * Upload all the chunks
   *
   * @param {FileWithPath[]} files
   * @param {number} defaultChunkSize
   * @param {number} defaultThreadCount
   * @memberof AppService
   */
  public async uploadDataInChunksNoMergeForAll(
    files: FileWithPath[],
    defaultChunkSize: number,
    defaultThreadCount: number,
    enableHash?: boolean,
  ) {
    const CHUNK_SIZE = defaultChunkSize * 1024 * 1024;
    const BATCH_SIZE = defaultThreadCount;
    let totalChunksForProgress = 0;
    let uploadedChunks = 0;
    const totalRetryAttempt = 5;

    // Generate unique session ID for this upload session
    const sessionId = `session_${Math.random().toString(36)}`;

    // Calculate total chunks for progress tracking
    for (const file of files) {
      totalChunksForProgress += Math.ceil(file.size / CHUNK_SIZE);
    }

    try {
      const filesMetadata: {
        originalFileName: string;
        uniqueFileName: string;
        totalChunks: number;
        uploadPath: string;
      }[] = [];

      // Create all chunk requests for all files
      const allChunkRequests: ChunkRequest[] = [];
      let totalHashStartTime = 0;

      // Set indeterminate progress if hash computation is enabled
      if (enableHash) {
        this.isIndeterminateProgressSignalHash.set(true);
        if (enableHash) {
          totalHashStartTime = performance.now();
          // eslint-disable-next-line no-console
          console.log('=== Starting hash computation for all files ===');
        }
      }

      for (const file of files) {
        const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
        const uploadPath = this.folderPathSignal() + (file.relativePath || '');

        // Generate unique file ID for each file (even single chunk files for consistency)
        const uniqueFileId = `${Math.random().toString(36)}`;
        const fileNameToUse = `${uniqueFileId}_${file.name}`;
        let fileCRC32Hash: string;

        if (enableHash) {
          // Compute CRC32C hash for the entire file
          try {
            const fileHashStartTime = performance.now();
            // eslint-disable-next-line no-console
            console.log(
              `Starting hash computation for file: ${file.name} (${(file.size / 1024 / 1024).toFixed(2)} MB)`,
            );
            fileCRC32Hash = await this.computeCRC32CBase64(file);
            this.hasher.init();
            const fileHashEndTime = performance.now();
            const fileHashDuration = (
              (fileHashEndTime - fileHashStartTime) /
              1000
            ).toFixed(3);
            // eslint-disable-next-line no-console
            console.log(
              `Hash computation completed for file: ${file.name} - Time taken: ${fileHashDuration} seconds`,
            );
          } catch (error) {
            // Generate a fallback identifier if hash computation fails
            fileCRC32Hash = `fallback_${Date.now()}_${Math.random().toString(36)}`;
            throw new Error(error as string | undefined);
          }
        } else {
          fileCRC32Hash = '';
        }
        // Create chunk requests for current file
        for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex++) {
          const start = chunkIndex * CHUNK_SIZE;
          const end = Math.min(start + CHUNK_SIZE, file.size);
          const chunk = file.slice(start, end);
          const fileType = file.type || '';

          const formData = new FormData();
          formData.append('fileChunk', chunk);
          formData.append('uploadGuid', sessionId);
          formData.append('fileName', fileNameToUse); // Use appropriate filename based on chunk count
          formData.append('chunkIndex', chunkIndex.toString());
          formData.append('totalChunks', totalChunks.toString());
          formData.append('uploadPath', uploadPath);
          if (fileCRC32Hash) {
            formData.append('crc32FileHashValue', fileCRC32Hash);
          }
          if ((file as File).lastModified) {
            formData.append(
              'lastModifiedDate',
              (file as File).lastModified.toString(),
            );
          } else {
            formData.append('lastModifiedDate', '');
          }
          formData.append('contentType', fileType);

          allChunkRequests.push({
            type: 'chunk',
            request: this.http.post<UploadResponse>(
              this.baseUrl + CONSTANTS_DETAILS.UPLOAD_CHUNKS_URL,
              formData,
            ),
            filename: fileNameToUse,
            chunkIndex: chunkIndex,
            totalChunks: totalChunks,
            formData: formData,
          });
        }

        filesMetadata.push({
          originalFileName: file.name,
          uniqueFileName: fileNameToUse,
          totalChunks: totalChunks,
          uploadPath: uploadPath,
        });
        if (enableHash) {
          const totalHashEndTime = performance.now();
          const totalHashDuration = (
            (totalHashEndTime - totalHashStartTime) /
            1000
          ).toFixed(3);
          // eslint-disable-next-line no-console
          console.log(
            `=== Total hash computation time for all ${files.length} files: ${totalHashDuration} seconds ===`,
          );
        }
      }

      // Reset indeterminate progress after hash computation is complete
      if (enableHash) {
        this.isIndeterminateProgressSignalHash.set(false);
      }

      // Set initial progress for upload phase
      this.uploadProgressSignal.set(1);

      // Upload all chunks
      const successfulChunks = new Set<string>(); // Use unique key: fileId_chunkIndex

      // Initial upload attempt for all chunks
      const initialResult = await this.uploadAllChunksWithRetry(
        allChunkRequests,
        BATCH_SIZE,
        successfulChunks,
        totalChunksForProgress,
        uploadedChunks,
        'Initial upload',
      );

      uploadedChunks = initialResult.uploadedChunks;

      // Retry failed chunks if any (up to 3 retry attempts)
      let failedRequests = initialResult.failedRequests;
      uploadedChunks = initialResult.uploadedChunks;

      for (
        let retryAttempt = 1;
        retryAttempt <= totalRetryAttempt && failedRequests.length > 0;
        retryAttempt++
      ) {
        const retryResult = await this.uploadAllChunksWithRetry(
          failedRequests,
          Math.max(1, Math.floor(BATCH_SIZE / 2)), // Lower batch size for retries
          successfulChunks,
          totalChunksForProgress,
          uploadedChunks,
          `Retry ${retryAttempt}`,
        );

        uploadedChunks = retryResult.uploadedChunks;
        failedRequests = retryResult.failedRequests;

        if (failedRequests.length === 0) {
          break;
        }
      }

      // If there are still failed chunks after all retries, report them
      if (failedRequests.length > 0) {
        this._dialogService.commonDialog({
          iconType: 'error',
          message: this._translateService.instant('someChunksFailedUpload'),
          singleButton: true,
        });
      }

      // Verify all chunks uploaded successfully for the session
      const allChunksUploaded = filesMetadata.every((fileInfo) =>
        Array.from({ length: fileInfo.totalChunks }, (_, i) => i).every(
          (chunkIndex) =>
            successfulChunks.has(`${fileInfo.uniqueFileName}_${chunkIndex}`),
        ),
      );

      if (!allChunksUploaded) {
        const missingChunks = filesMetadata.filter(
          (fileInfo) =>
            !Array.from({ length: fileInfo.totalChunks }, (_, i) => i).every(
              (chunkIndex) =>
                successfulChunks.has(
                  `${fileInfo.uniqueFileName}_${chunkIndex}`,
                ),
            ),
        );
        throw new Error(
          `Upload failed: Some chunks could not be uploaded for ${missingChunks.length} file(s)`,
        );
      }

      // Check if we need to call merge API
      // Skip merge if all files have only 1 chunk (single chunk uploads)
      const needsMerge = filesMetadata.some(
        (fileInfo) => fileInfo.totalChunks > 1,
      );

      // Merge session call without retry
      let mergeSuccess = false;
      let fewFileFailed = false;
      let allFilesFailed = false;

      if (needsMerge) {
        // Set indeterminate progress to true during merge phase
        this.isIndeterminateProgressSignal.set(true as boolean);

        try {
          const mergeResponse = await lastValueFrom(
            this.mergeSessionCall(sessionId),
          );

          // Check if response indicates success
          const response = mergeResponse as unknown as MergeResponse;
          if (response && response.isSuccess === false) {
            if (
              response.hashMismatchFiles &&
              response.hashMismatchFiles.length > 0
            ) {
              response.defectiveFiles?.push(...response.hashMismatchFiles);
            }
            this._dialogService.commonDialog({
              iconType: 'error',
              message: response,
              singleButton: true,
              listRequired: true,
            });
            mergeSuccess = false;
            fewFileFailed = true;
            allFilesFailed = false;
            this.uploadCompleteSignal.set(true);
            this.uploadProgressSignal.set(0);
          } else {
            mergeSuccess = true;
            fewFileFailed = false;
            allFilesFailed = false;
          }
        } catch {
          allFilesFailed = true;
        }

        // Reset indeterminate progress signal after merge phase completes
        this.isIndeterminateProgressSignal.set(false);
      } else {
        // Single chunk upload - no merge needed, mark as success
        mergeSuccess = true;
        fewFileFailed = false;
        allFilesFailed = false;
      }

      if (mergeSuccess && !fewFileFailed && !allFilesFailed) {
        this.uploadProgressSignal.set(100);
        this._dialogService.commonDialog({
          iconType: 'info',
          message: this._translateService.instant(
            'allFilesUploadedSuccessfully',
          ),
          singleButton: true,
        });

        // Trigger upload complete signal to refresh file explorer
        this.uploadCompleteSignal.set(true);
        setTimeout(() => {
          this.uploadCompleteSignal.set(false);
        }, 100);

        // Reset progress after a short delay to show completion
        setTimeout(() => {
          this.uploadProgressSignal.set(0);
          this.isIndeterminateProgressSignal.set(false);
        }, 20);
      } else if (!fewFileFailed && !mergeSuccess && allFilesFailed) {
        this._dialogService.commonDialog({
          iconType: 'error',
          message: this._translateService.instant('failedToMergeFiles'),
          singleButton: true,
        });
      }
    } catch (error) {
      this.uploadProgressSignal.set(0);
      this.isIndeterminateProgressSignal.set(false);
      this._dialogService.commonDialog({
        iconType: 'error',
        message: error instanceof Error ? error.message : String(error),
        singleButton: true,
      });
    }
  }

  /**
   * API call is done to update the new name
   *
   * @param {string} currentFullPath
   * @param {string} newFullPath
   * @param {boolean} isFolder
   * @return {*}
   * @memberof AppService
   */
  public renameFilesFolders(
    currentFullPath: string,
    newFullPath: string,
    isFolder: boolean,
  ) {
    const url = `${this.baseUrl}${CONSTANTS_DETAILS.RENAME_URL}`;
    const body = {
      objectPath: currentFullPath,
      newObjectName: newFullPath,
      isFolder: isFolder,
    };
    return this.http.put(url, body, { responseType: 'text' });
  }

  /**
   * API call is done to update the fav status
   *
   * @param {string} objectPath
   * @param {boolean} isFavorite
   * @return {*}
   * @memberof AppService
   */
  public updateFavoriteStatus(objectPath: string, isFavorite: boolean) {
    const url = `${this.baseUrl}${CONSTANTS_DETAILS.FAVORITE_URL}`;
    const body = {
      objectPath: objectPath,
      isFavorite: isFavorite,
    };
    return this.http.put(url, body, { responseType: 'text' });
  }

  /**
   * Determines if an error is retryable based on error type and status
   *
   * @private
   * @param {Error | unknown} error
   * @return {boolean}
   * @memberof AppService
   */
  private isRetryableError(error: Error | unknown): boolean {
    // Network-related errors that are retryable
    const retryableErrors = [
      'NetworkError',
      'TimeoutError',
      'ConnectionError',
      'ECONNRESET',
      'ENOTFOUND',
      'ECONNREFUSED',
      'ETIMEDOUT',
    ];

    // HTTP status codes that are retryable
    const retryableStatusCodes = [
      408, // Request Timeout
      429, // Too Many Requests
      500, // Internal Server Error
      502, // Bad Gateway
      503, // Service Unavailable
      504, // Gateway Timeout
      507, // Insufficient Storage
      509, // Bandwidth Limit Exceeded
      510, // Not Extended
    ];

    if (error && typeof error === 'object') {
      // Check for HTTP error status
      if ('status' in error) {
        const status = (error as { status: unknown }).status;
        if (typeof status === 'number') {
          if (retryableStatusCodes.includes(status)) {
            return true;
          }
          // Don't retry client errors (4xx except specific ones)
          if (
            status >= 400 &&
            status < 500 &&
            !retryableStatusCodes.includes(status)
          ) {
            return false;
          }
        }
      }

      // Check error name/type
      if ('name' in error) {
        const errorName = (error as { name: unknown }).name;
        if (
          typeof errorName === 'string' &&
          retryableErrors.some((retryable) => errorName.includes(retryable))
        ) {
          return true;
        }
      }

      // Check error message for network-related issues
      if ('message' in error) {
        const message = (error as { message?: unknown }).message;
        const messageStr =
          typeof message === 'string' ? message.toLowerCase() : '';
        if (
          retryableErrors.some((retryable) =>
            messageStr.includes(retryable.toLowerCase()),
          )
        ) {
          return true;
        }
      }
    }

    // Default to retryable for unknown errors to be safe
    return true;
  }

  /**
   * Uploads all chunks (from multiple files) with retry logic
   *
   * @private
   * @param {ChunkRequest[]} chunkRequests
   * @param {number} batchSize
   * @param {Set<string>} successfulChunks - Set of successful chunk keys (filename_chunkIndex)
   * @param {number} totalChunksForProgress
   * @param {number} uploadedChunks
   * @param {string} context - Context for logging (e.g., 'initial' or 'retry')
   * @return {Promise<{ uploadedChunks: number; failedRequests: ChunkRequest[] }>}
   * @memberof AppService
   */
  private async uploadAllChunksWithRetry(
    chunkRequests: ChunkRequest[],
    batchSize: number,
    successfulChunks: Set<string>,
    totalChunksForProgress: number,
    uploadedChunks: number,
    context = 'upload',
  ): Promise<{ uploadedChunks: number; failedRequests: ChunkRequest[] }> {
    const failedRequests: ChunkRequest[] = [];
    const nonRetryableErrors: {
      chunkIndex: number;
      filename: string;
      error: Error | unknown;
    }[] = [];
    let currentUploadedChunks = uploadedChunks;

    return new Promise<{
      uploadedChunks: number;
      failedRequests: ChunkRequest[];
    }>((resolve, reject) => {
      from(chunkRequests)
        .pipe(
          mergeMap(
            (req) =>
              req.request.pipe(
                map(
                  (response: UploadResponse) =>
                    ({
                      success: true,
                      response,
                      chunkIndex: req.chunkIndex,
                      filename: req.filename,
                      totalChunks: req.totalChunks,
                    }) as ChunkResult,
                ),
                catchError((error: Error | unknown) =>
                  of({
                    success: false,
                    error,
                    chunkIndex: req.chunkIndex,
                    filename: req.filename,
                    totalChunks: req.totalChunks,
                    requestData: req,
                  } as ChunkResult),
                ),
              ),
            batchSize,
          ),
        )
        .subscribe({
          next: (result: ChunkResult) => {
            if (result.success && result.response) {
              successfulChunks.add(`${result.filename}_${result.chunkIndex}`); // Use unique key
              currentUploadedChunks++;
              const percent = Math.round(
                (currentUploadedChunks / totalChunksForProgress) * 100,
              );
              this.uploadProgressSignal.set(percent);
            } else if (!result.success && result.requestData) {
              // Check if the error is retryable
              if (this.isRetryableError(result.error)) {
                failedRequests.push(result.requestData);
              } else {
                nonRetryableErrors.push({
                  chunkIndex: result.chunkIndex,
                  filename: result.filename,
                  error: result.error,
                });
              }
            }
          },
          error: (error: Error | unknown) => {
            // Check if this stream error is retryable
            if (!this.isRetryableError(error)) {
              reject(error);
            }
          },
          complete: () => {
            // If we have non-retryable errors, reject the promise
            if (nonRetryableErrors.length > 0) {
              const errorMessage = `${context}: ${nonRetryableErrors.length} chunks failed with non-retryable errors`;
              reject(
                new Error(
                  errorMessage +
                    ': ' +
                    nonRetryableErrors
                      .map((e) => `Chunk ${e.chunkIndex} of ${e.filename}`)
                      .join(', '),
                ),
              );
              return;
            }
            resolve({ uploadedChunks: currentUploadedChunks, failedRequests });
          },
        });
    });
  }

  /**
   * Merge all files in a session after all chunks are uploaded
   *
   * @param {string} sessionId - The unique session ID for this upload session
   * @return {*}
   * @memberof AppService
   */
  public mergeSessionCall(sessionId: string) {
    const url = `${this.baseUrl}${CONSTANTS_DETAILS.MERGE_SESSION_URL}`;
    const body = {
      uploadGuid: sessionId,
    };
    return this.http.post(url, body);
  }

  /**
   * Creating hash value for the file that are uploaded
   *
   * @param {File} file
   * @return {*}  {Promise<string>}
   * @memberof AppService
   */
  async computeCRC32CBase64(file: File): Promise<string> {
    // Validate that the file parameter is a valid File/Blob object
    if (!file) {
      throw new Error('File parameter is null or undefined');
    }

    if (typeof file !== 'object' || !('size' in file) || !('type' in file)) {
      throw new Error('Invalid file parameter: must be a File or Blob object');
    }
    try {
      const supportsFileStream = typeof (file as File).stream === 'function';
      if (supportsFileStream) {
        const reader = file.stream().getReader();
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          this.hasher.update(value);
        }
      }
      return this.hexCrc32ToBase64(this.hasher.digest());
    } catch (error) {
      throw new Error(
        `CRC32C computation failed for ${file.name}: ${error instanceof Error ? error.message : String(error)}`,
      );
    }
  }

  /**
   * Convert hexadecimal CRC32 string to Base64-encoded string
   *
   * @private
   * @param {string} crcHex
   * @return {*}  {string}
   * @memberof AppService
   */
  private hexCrc32ToBase64(crcHex: string): string {
    const crcNum = parseInt(crcHex, 16) >>> 0;
    const buf = new ArrayBuffer(4);
    const view = new DataView(buf);
    view.setUint32(0, crcNum, false);
    const bytes = new Uint8Array(buf);
    return btoa(String.fromCharCode(...bytes));
  }

  /**
   * Get API call for chunk size and theard count
   *
   * @return {*}  {Promise<ChunkConfig>}
   * @memberof AppService
   */
  public async getChunkConfig(): Promise<ChunkConfig> {
    return lastValueFrom(
      this.http.get<ChunkConfig>(
        this.baseUrl + CONSTANTS_DETAILS.GET_CHUNK_CONFIG_URL,
      ),
    ).catch((err) => {
      this._dialogService.commonDialog({
        iconType: 'error',
        message:
          this._translateService.instant('chunkConfigFetchError') + err.message,
        singleButton: true,
      });
      throw err;
    });
  }

  /**
   * Update the chunk and thread count.
   *
   * @param {number} chunkSizeInMB
   * @param {number} maxConcurrentRequests
   * @param {boolean} enableHashValue
   * @return {*}
   * @memberof AppService
   */
  public updateChunkConfig(
    chunkSizeInMB: number,
    maxConcurrentRequests: number,
    enableHashValue?: boolean,
  ) {
    const url = `${this.baseUrl}${CONSTANTS_DETAILS.UPDATE_CHUNK_CONFIG_URL}`;
    const body = {
      chunkSizeInMB: chunkSizeInMB,
      maxConcurrentRequests: maxConcurrentRequests,
      isHashEnabled: enableHashValue,
    };
    return this.http.put(url, body, { responseType: 'text' }).pipe(
      catchError((err) => {
        this._dialogService.commonDialog({
          iconType: 'error',
          message:
            this._translateService.instant('chunkConfigUpdateError') +
            err.message,
          singleButton: true,
        });
        throw err;
      }),
    );
  }

  /**
   * Download Summary report
   *
   * @memberof AppService
   */
  public downloadSummaryReport(): void {
    const url = `${this.baseUrl}${CONSTANTS_DETAILS.GENERATE_BUCKET_REPORT_URL}`;
    this.http
      .get(url, {
        responseType: 'blob',
        headers: new HttpHeaders({
          Accept:
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        }),
      })
      .pipe(
        catchError((err) => {
          this._snackbarService.open({
            message: this._translateService.instant('downloadFailed'),
            type: 'error',
          });
          throw err;
        }),
      )
      .subscribe((blob) => {
        try {
          const fileName = `directory-summary-report.xlsx`;
          const urlObject = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = urlObject;
          a.download = fileName;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(urlObject);
        } catch {
          this._snackbarService.open({
            message: this._translateService.instant('downloadFailed'),
            type: 'error',
          });
        }
      });
  }
}
